import { useEffect, useState } from 'react'
import {
  SelectChangeEvent,
  Box,
  styled,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  Paper,
  TableBody,
  TableCell,
  tableCellClasses,
  Grid,
  useMediaQuery,
  useTheme,
  Typography,
  Button,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material'
import TextInformation from '../../components/TextInformation'
import { CustomSelect } from '../../components/CustomSelect'
import FancyInput from '../../components/FancyInput'
import StepsBtn from '../../components/StepsBtn'
import TotalToPay from '../../components/TotalToPay'
import { formatCreditCardNumber, formatExpirationDate } from '../../utils/formatCreditCardNumber'
import TableLayout from '../../layouts/TableLayout'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css/navigation'
import 'swiper/css'
import { Navigation, Mousewheel } from 'swiper'
import Lightbox from 'yet-another-react-lightbox'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import { useApp } from '../../hooks/useApp'
import { usePayMethods } from '../../queries/usePaymentQueries'
import { useCountries } from '../../queries/useUtilQueries'
import Progress from '../../components/Progress'
import TilopayPayment from './TilopayPayment'
import { setFormDataKey, useFormData } from '../../queries/userGlobalQueries'
import { queryClient } from '../../queries/config'
import React from 'react'
import { useUI } from '../../hooks/useUI'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14
  }
}))

const BoxStyled = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: ' 1fr 1fr',
  alignItems: 'center',
  width: '100%',
  marginTop: 10,
  rowGap: theme.spacing(2),
  columnGap: theme.spacing(4),
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr'
  }
}))

const LightboxStyled = styled(Lightbox)(({ theme }) => ({
  '& .yarl__container': {
    backgroundColor: '#000000b0'
  },
  '& img': {
    width: '600px !important',
    height: '400px !important',
    maxWidth: '600px !important',
    maxHeight: '400px !important'
  }
}))

interface TilopayPaymentProps {
  apiKey: string
  apiuser: string
  password: string
  cartData: any
  currency: string
  onPaymentComplete: () => void
}

const Payments = ({ handleBack, handleNext, cartData, colegial, setPayOrder, currency }: any) => {
  const { t } = useTranslation()
  const { iDCompany, setErrorMsg } = useApp()

  const theme = useTheme()
  const isMd = useMediaQuery(theme.breakpoints.down('md'))
  const isXl = useMediaQuery(theme.breakpoints.up('xl'))

  const { data: formData } = useFormData()

  const [cardNumberInput, setCardNumberInput] = useState(formData.billingMethodCard.cardNumber)
  const [cardUserNameInput, setCardUserNameInput] = useState(formData.billingMethodCard.cardName)
  const [cardExpirationDateInput, setCardExpirationDateInput] = useState(
    formData.billingMethodCard.cardExpiry
  )
  const [cardCVVInput, setCardCVVInput] = useState(formData.billingMethodCard.cardCvc)
  const [select, setSelect] = useState(formData.payMethod)
  const [formatCvv, setFormatCvv] = useState<boolean>(true)
  const [cvvError, setCVVError] = useState<string | null>(null)
  const [cardType, setCardType] = useState<string>('')
  const [formatCard, setFormatCard] = useState<boolean>(true)
  const [expirationDateError, setExpirationDateError] = useState<string | null>(null)

  const [urlImg, setUrlImg] = useState<string[] | undefined>(
    formData.photoGallery ? formData.photoGallery : undefined
  )
  const [open, setOpenLightbox] = useState<boolean>(false)
  const [indexImg, setIndexImg] = useState<number>(0)

  const [openModal, setOpen] = React.useState(false)
  const [openDiscardProof, setOpenDiscardProof] = React.useState(false)
  const [preview, setPreview] = React.useState<string | null>(null)
  const [eventSelect, setEventSelect] = useState<SelectChangeEvent | null>(null)
  const [file, setFile] = useState<File>()
  const [imageLoading, setImageLoading] = useState(true)
  const { darkMode } = useUI()

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      //! File size validation 5MB
      if (file.size > 5 * 1024 * 1024) {
        setErrorMsg && setErrorMsg(t('fileTooLarge'))
        e.target.value = ''
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        e.target.value = ''
        setPreview(reader.result as string)
        setOpen(true)
      }
      reader.readAsDataURL(file)
      setFile(file)
    }
  }

  const discardProof = async () => {
    queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
      ...old,
      urlProof: '',
      proofFile: null
    }))

    handleChange(eventSelect as SelectChangeEvent)
  }

  const handleClose = () => {
    setOpen(false)
    setPreview(null)
  }

  const handleConfirm = async () => {
    if (!preview) return
    handleClose()

    queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
      ...old,
      proofFile: file
    }))
  }

  const [tarjetaIsReady, setTarjetaIsReady] = useState<boolean>(false)
  const [tilopayPaymentInfo, setTilopayPaymentInfo] = useState<TilopayPaymentProps>({
    apiKey: '',
    apiuser: '',
    password: '',
    cartData: cartData,
    currency: currency,
    onPaymentComplete: () => {
      setTarjetaIsReady(true)
    }
  })

  const [cardAvailable, setCardAvailable] = useState<boolean>(false)

  const countryCompanyId = iDCompany?.body?.content[0]?.country

  // Queries
  const { data: payMethodsData, isLoading: isLoadingPayMethods } = usePayMethods(
    iDCompany?.body?.content[0]?.id
  )

  const { data: countryData, isLoading: isLoadingCountry } = useCountries(0)

  const countryInfo = countryData?.body?.content?.find((item: any) => item.id === countryCompanyId)

  useEffect(() => {
    document.documentElement.scrollTop = 0
  }, [])

  useEffect(() => {
    if (
      payMethodsData?.paymentOptions !== undefined &&
      payMethodsData?.paymentOptions.length > 0 &&
      !select
    ) {
      const firstOption = payMethodsData.paymentOptions[0]
      queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
        ...old,
        payMethod: firstOption
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(' ', '')
          .toLocaleUpperCase(),
        payMethodLabel: firstOption
      }))

      setSelect(firstOption)
    } else {
      queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
        ...old,
        payMethod: select
      }))
    }

    const cardData = payMethodsData?.methods?.card?.[0]

    const hasValidCardCredentials = cardData && {
      apiKey: cardData.apiKey?.trim(),
      user: cardData.user?.trim(),
      password: cardData.password?.trim()
    }

    if (hasValidCardCredentials && Object.values(hasValidCardCredentials).every((value) => value)) {
      setCardAvailable(true)
      setTilopayPaymentInfo((prev) => ({
        ...prev,
        ...hasValidCardCredentials
      }))
    } else {
      setCardAvailable(false)
    }
  }, [payMethodsData, select, formData])

  const handleChange = (event: SelectChangeEvent, proofFile?: File) => {
    const value = event.target.value as string

    if (proofFile) {
      setOpenDiscardProof(true)
      return
    }

    queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
      ...old,
      payMethod: value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(' ', '')
        .toLocaleUpperCase(),
      payMethodLabel: value
    }))
    setSelect(value)
  }

  const handleInputCardNumber = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const inputValue = event.target.value
    const formattedPhoneNumber = formatCreditCardNumber(inputValue, setCardType)
    setCardNumberInput(formattedPhoneNumber)

    queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
      ...old,
      cardNumber: inputValue
    }))

    if (
      (cardType === 'dinersclub' && inputValue.replace(/\s/g, '').length >= 14) ||
      (cardType === 'amex' && inputValue.replace(/\s/g, '').length >= 15) ||
      (cardType === 'default' && inputValue.replace(/\s/g, '').length >= 16)
    ) {
      setFormatCard(true)
    } else {
      setFormatCard(false)
    }
  }

  const handleInputCardExpirationDate = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const inputValue = formatExpirationDate(event.target.value)
    const isValidFormat = /^\d{0,2}\/\d{0,2}$/.test(inputValue)

    if (isValidFormat || inputValue === '') {
      setCardExpirationDateInput(inputValue)
      queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
        ...old,
        cardExpirationDate: inputValue
      }))

      if (inputValue === '') {
        setExpirationDateError(null)
      } else {
        const currentDate = new Date()
        const currentYear = currentDate.getFullYear() % 100
        const currentMonth = currentDate.getMonth() + 1

        const [inputMonth, inputYear] = inputValue.split('/').map(Number)

        if (inputYear < currentYear || (inputYear === currentYear && inputMonth < currentMonth)) {
          setExpirationDateError(t('dateFormat'))
        } else {
          setExpirationDateError(null)
        }
      }
    } else {
      setCardExpirationDateInput(inputValue)
    }
  }

  const nextBtn = () => {
    handleNext()
  }

  const handleLightbox = (index: number) => {
    setOpenLightbox(true)
    setIndexImg(index)
  }

  const urlImgs = urlImg?.map((img) => ({
    src: img
  }))

  const getMessageByCardType = (cardType: string): string => {
    switch (cardType) {
      case 'amex':
        return t('amex')
      case 'dinersclub':
        return t('dinersclub')
      default:
        return t('default')
    }
  }

  const isLoading = isLoadingPayMethods || isLoadingCountry

  if (isLoading) {
    return <Progress />
  }

  return (
    <>
      <Grid container spacing={4} sx={{ height: '100%', marginTop: 0 }}>
        <Grid
          item
          xs={12}
          lg={8}
          sx={{
            borderRight: {
              lg: '1px solid'
            },
            pr: 3,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {(isMd || isXl) && <TextInformation title={`${t('paymentMethod')}`} />}
          <Stack>
            <Box py={3}>
              <Box px={3} py={0}>
                <CustomSelect
                  listItems={payMethodsData?.paymentOptions || []}
                  value={select}
                  onChange={(event) => {
                    handleChange(event, formData.proofFile)
                    setEventSelect(event)
                  }}
                  disabledEmpty
                  label={t('paymentMethod')}
                />
              </Box>
              {payMethodsData?.paymentOptions !== undefined &&
              payMethodsData?.paymentOptions?.length > 0 ? (
                <>
                  {select === 'Tarjeta' ? (
                    {
                      ...(cardAvailable ? (
                        <TilopayPayment
                          apiKey={tilopayPaymentInfo.apiKey}
                          apiuser={tilopayPaymentInfo.apiuser}
                          password={tilopayPaymentInfo.password}
                          cartData={cartData}
                          currency={currency}
                          onPaymentComplete={() => {
                            setTarjetaIsReady(true)
                            setPayOrder(true)
                          }}
                        />
                      ) : (
                        <Box sx={{ padding: '16px' }}>
                          <TextInformation title={`${t('paymentMethodNotAvailable')}`} />
                        </Box>
                      ))
                    }
                  ) : (
                    <Box hidden={select !== 'Tarjeta'}>
                      <BoxStyled>
                        <FancyInput
                          required
                          autoComplete="nope"
                          type={'password'}
                          value={cardNumberInput}
                          onChange={(event) => handleInputCardNumber(event)}
                          inputProps={{
                            maxLength: 19
                          }}
                          label={t('card')}
                          validation={[
                            {
                              validate: () => formatCard,
                              msg: getMessageByCardType(cardType)
                            }
                          ]}
                        />

                        <FancyInput
                          required
                          value={cardUserNameInput}
                          onChange={(event) => {
                            const inputValue = event.target.value
                            const filteredValue = inputValue.replace(/[0-9]/g, '')
                            setCardUserNameInput(filteredValue)
                            queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
                              ...old,
                              cardUserName: filteredValue
                            }))
                          }}
                          label={t('cardholderName')}
                        />

                        <FancyInput
                          required
                          value={cardExpirationDateInput}
                          onChange={(event) => handleInputCardExpirationDate(event)}
                          label={t('dueDate')}
                          validation={[
                            {
                              validate: () => formatCvv,
                              msg: `${t('invalidFormat')}`
                            }
                          ]}
                          error={!!expirationDateError}
                          helperText={expirationDateError}
                        />

                        <FancyInput
                          type={'password'}
                          autoComplete="nope"
                          onlyNumber
                          inputProps={{
                            maxLength: 3
                          }}
                          label={t('cvv')}
                          value={cardCVVInput}
                          onChange={(event) => {
                            const value = event.target.value
                            if (value.length <= 3) {
                              setCardCVVInput(value)
                              queryClient.setQueryData([setFormDataKey], (old: FormData) => ({
                                ...old,
                                cardCVV: value
                              }))
                              if (value.length < 3) {
                                setCVVError(t('cvvFormat'))
                              } else {
                                setCVVError(null)
                              }
                            }
                          }}
                          required
                          error={!!cvvError}
                          helperText={cvvError}
                        />
                      </BoxStyled>
                    </Box>
                  )}
                  <Box hidden={select === 'Tarjeta'}>
                    <>
                      {select === 'Sinpe Móvil' ? (
                        <>
                          {' '}
                          <TableLayout countryCode={countryInfo?.areaCode}>
                            <TableContainer component={Paper}>
                              <Table aria-label="simple table">
                                <TableHead>
                                  <TableRow>
                                    <StyledTableCell>{t('half')}</StyledTableCell>
                                    <StyledTableCell>{t('account')}</StyledTableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {payMethodsData?.methods?.account
                                    ?.filter((row: any) => row.type === 4)
                                    .map((row: any) => (
                                      <TableRow key={row.name}>
                                        <TableCell width={200}>{row.name}</TableCell>
                                        <TableCell>{row.account}</TableCell>
                                      </TableRow>
                                    ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </TableLayout>
                        </>
                      ) : (
                        <>
                          <TableLayout countryCode={countryInfo?.areaCode}>
                            <TableContainer component={Paper}>
                              <Table aria-label="Bank table">
                                <TableHead>
                                  <TableRow>
                                    <StyledTableCell>{t('bank')}</StyledTableCell>
                                    <StyledTableCell>{t('account')}</StyledTableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {payMethodsData?.methods?.account
                                    ?.filter((row: any) => row.type === 3)
                                    .map((row: any) => (
                                      <TableRow key={row.id}>
                                        <TableCell width={200}>{row.name}</TableCell>
                                        <TableCell width={50}>{row.account}</TableCell>
                                      </TableRow>
                                    ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </TableLayout>
                        </>
                      )}
                      <Box mt={2} textAlign={'center'}>
                        {/* {urlProof ? (
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <img
                              src={urlProof}
                              alt="Uploaded Proof"
                              onClick={() => setOpen(true)}
                              style={{
                                maxWidth: '10%',
                                marginBottom: '8px',
                                display: 'block',
                                margin: '0 auto 8px auto',
                                cursor: 'pointer'
                              }}
                            />
                            <Box height={4}></Box>{' '}
                            <Button variant="contained" component="label" color="primary">
                              {t('replaceProof')}
                              <input
                                type="file"
                                accept=".png,.jpg,.jpeg"
                                hidden
                                onChange={handleFileChange}
                              />
                            </Button>
                          </Box> */}
                        {formData.proofFile ? (
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <img
                              src={URL.createObjectURL(formData.proofFile)}
                              alt="Uploaded Proof"
                              onClick={() => setOpen(true)}
                              style={{
                                maxWidth: '10%',
                                marginBottom: '8px',
                                display: 'block',
                                margin: '0 auto 8px auto',
                                cursor: 'pointer'
                              }}
                            />
                            <Box height={4}></Box>{' '}
                            <Button variant="contained" component="label" color="primary">
                              {t('replaceProof')}
                              <input
                                type="file"
                                accept=".png,.jpg,.jpeg"
                                hidden
                                onChange={handleFileChange}
                              />
                            </Button>
                          </Box>
                        ) : (
                          <Button variant="contained" component="label" color="primary">
                            {t('attachProof')}
                            <input
                              type="file"
                              accept=".png,.jpg,.jpeg"
                              hidden
                              onChange={handleFileChange}
                            />
                          </Button>
                        )}
                      </Box>
                      <Box mt={2} textAlign={'center'}>
                        <Typography
                          variant="body2"
                          className={darkMode ? 'text-gray-300' : 'text-gray-700'}
                        >
                          {t('youAreIncorporatedNote')}
                        </Typography>
                      </Box>
                    </>
                  </Box>
                </>
              ) : (
                <Box>
                  <Typography variant="h6">
                    {<TextInformation title={`${t('paymentMethodsNotAvailable')}`} />}
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
          <TotalToPay amount={colegial.BookingAdmin ? 0 : cartData.allAmount} currency={currency} />
          <StepsBtn
            onClickBack={handleBack}
            onClickNext={nextBtn}
            disabledNext={
              (select === 'Tarjeta' && !tarjetaIsReady) || !payMethodsData?.paymentOptions?.length
            }
          />
        </Grid>
        <Grid
          item
          xs={12}
          lg={4}
          sx={{
            overflow: 'hidden',
            pt: '20px!important',
            pb: '15px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Stack
            sx={{
              maxHeight: {
                lg: '52vh'
              }
            }}
          >
            {formData.area &&
              (urlImg !== undefined && urlImg?.length > 2 ? (
                <Swiper
                  spaceBetween={10}
                  slidesPerView={1}
                  navigation
                  mousewheel={true}
                  direction={'horizontal'}
                  modules={[Navigation, Mousewheel]}
                  breakpoints={{
                    425: {
                      slidesPerView: 2,
                      direction: 'horizontal'
                    },
                    600: {
                      slidesPerView: 3,
                      direction: 'horizontal'
                    },
                    1201: {
                      slidesPerView: 3,
                      direction: 'vertical'
                    }
                  }}
                >
                  {urlImg?.map((img, i) => (
                    <SwiperSlide key={i}>
                      <img alt="name" src={img} onClick={() => handleLightbox(i)} />
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : (
                urlImg?.map((img, i) => (
                  <div key={i}>
                    <img
                      alt="name"
                      height="175px"
                      src={img}
                      onClick={() => handleLightbox(i)}
                      className="img-without-slider"
                    />
                  </div>
                ))
              ))}
          </Stack>
        </Grid>
      </Grid>
      <LightboxStyled
        open={open}
        index={indexImg}
        close={() => setOpenLightbox(false)}
        slides={urlImgs}
        carousel={{ finite: true }}
        controller={{ closeOnBackdropClick: true }}
      />
      <Dialog open={openModal} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('preview')}</DialogTitle>
        <DialogContent>
          {preview ? (
            <>
              {imageLoading && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                </Box>
              )}
              <img
                src={preview}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  display: imageLoading ? 'none' : 'block'
                }}
                onLoad={() => setImageLoading(false)}
              />
            </>
          ) : (
            <>
              {imageLoading && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                </Box>
              )}
              <img
                src={formData.proofFile ? URL.createObjectURL(formData.proofFile) : ''}
                alt="Uploaded Proof"
                style={{
                  maxWidth: '100%',
                  display: imageLoading ? 'none' : 'block'
                }}
                onLoad={() => setImageLoading(false)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            {t('cancel')}
          </Button>
          <Button
            onClick={async () => {
              if (!preview) {
                handleClose()
                return
              }
              await handleConfirm()
              setOpen(false)
            }}
            color="primary"
          >
            {preview ? t('confirm') : t('accept')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDiscardProof}
        onClose={() => setOpenDiscardProof(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('discardProof')}</DialogTitle>
        <DialogContent>
          <Typography>{t('discardProofConfirmation')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDiscardProof(false)} color="secondary">
            {t('cancel')}
          </Button>
          <Button
            onClick={async () => {
              await discardProof()
              setOpenDiscardProof(false)
            }}
            color="primary"
          >
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default Payments
